/**
 * Language Switcher Component
 *
 * Shows configured portal languages with flags.
 * Clicking a language switches the i18n language and sets ?lang= URL param.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { CountryFlag } from '@/components/common/CountryFlag';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_MAP,
  type Language,
} from '@/i18n/languages';
import type { PortalLanguage } from '@/types/portal';

interface LanguageSwitcherProps {
  /** If provided, only show these language codes. Otherwise show all. */
  supportedLanguages?: string[];
  /** If provided, show portal-configured languages. */
  languages?: PortalLanguage[];
}

export const LanguageSwitcher = ({ supportedLanguages, languages }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const usePortalMode = languages && languages.length > 0;

  // Resolve display languages
  const displayLanguages = useMemo(() => {
    if (usePortalMode) {
      return languages.map((l) => {
        const info = LANGUAGE_MAP.get(l.language);
        return {
          code: l.language,
          name: info?.name ?? l.language,
          country: info?.country ?? 'DE',
        };
      });
    }
    const filtered = supportedLanguages
      ? SUPPORTED_LANGUAGES.filter((l) => supportedLanguages.includes(l.code))
      : SUPPORTED_LANGUAGES;
    return filtered.map((l) => ({ code: l.code, name: l.name, country: l.country }));
  }, [usePortalMode, languages, supportedLanguages]);

  const defaultLang: Language = { code: 'de', name: 'Deutsch', nameEn: 'German', country: 'DE' };
  const currentLang = LANGUAGE_MAP.get(i18n.language) ?? LANGUAGE_MAP.get('de') ?? defaultLang;

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    // Update ?lang= URL parameter
    const url = new URL(window.location.href);
    url.searchParams.set('lang', code);
    // Remove legacy ?iso= param if present
    url.searchParams.delete('iso');
    window.history.replaceState({}, '', url.toString());
    setIsOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-ghost btn-sm gap-1.5"
        aria-label="Switch language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <CountryFlag country={currentLang.country} className="h-4 w-6" />
        <span className="text-xs font-medium uppercase">{currentLang.code}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Select language"
          className="absolute right-0 top-full z-50 mt-1 max-h-72 w-max min-w-48 overflow-y-auto rounded-card border border-base-300 bg-base-200 py-1 shadow-lg"
        >
          {displayLanguages.map((lang) => (
            <li key={lang.code}>
              <button
                role="option"
                aria-selected={i18n.language === lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm whitespace-nowrap transition-colors hover:bg-base-300 ${
                  i18n.language === lang.code ? 'bg-base-300 font-medium' : ''
                }`}
              >
                <CountryFlag country={lang.country} className="h-4 w-6" />
                <span>{lang.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
